/**
 * Shared HTTP Types and Error Handling
 * Centralized types for HTTP requests, responses, and errors
 */

function messageFromErrorBody(data: unknown): { message?: string; code?: string } {
  if (!data || typeof data !== 'object') return {};
  const d = data as Record<string, unknown>;
  const detail = d['detail'];

  if (typeof detail === 'string') {
    return { message: detail };
  }

  if (Array.isArray(detail)) {
    const text = detail
      .map((item) => {
        if (item && typeof item === 'object' && typeof (item as { msg?: string }).msg === 'string') {
          return (item as { msg: string }).msg.replace(/^Value error,\s*/i, '').trim();
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
    return text ? { message: text } : {};
  }

  if (detail && typeof detail === 'object') {
    const nested = detail as Record<string, unknown>;
    if (typeof nested['message'] === 'string') {
      return {
        message: nested['message'],
        code: typeof nested['code'] === 'string' ? nested['code'] : undefined,
      };
    }
  }

  if (typeof d['message'] === 'string') {
    return { message: d['message'] };
  }

  return {};
}

/**
 * HTTP Error class for typed error handling
 */
export class HttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public message: string,
    public data?: unknown,
    /** Structured error code from a { code, message } backend envelope */
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  static fromResponse(response: Response, data?: unknown): HttpError {
    const parsed = messageFromErrorBody(data);
    const message = parsed.message || response.statusText;
    const code = parsed.code;

    return new HttpError(response.status, response.statusText, message, data, code);
  }

  static fromError(error: unknown): HttpError {
    if (error instanceof HttpError) {
      return error;
    }
    if (error instanceof Error) {
      return new HttpError(500, 'Internal Server Error', error.message);
    }
    return new HttpError(500, 'Internal Server Error', 'An unknown error occurred');
  }

  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  isNetworkError(): boolean {
    return this.status === 0;
  }
}

/**
 * HTTP Request configuration
 */
export interface RequestConfig {
  headers?: HeadersInit;
  signal?: AbortSignal;
  timeout?: number;
  /** e.g. 'include' for cookie-based dev tools API */
  credentials?: RequestCredentials;
}

/**
 * HTTP Response type
 */
export interface HttpResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Query parameters type
 */
export type QueryParams = Record<string, string | number | boolean | (string | number | boolean)[] | null | undefined>;
